import React, { useEffect, useRef, useState, type JSX } from "react";
import { useParams } from "react-router-dom";
import { differenceInSeconds, subMinutes } from "date-fns";
import { Loader } from "@beholos/holoscommon";
import { getUserClassByHash, verifyUserClassByHash } from "../../services/user.service";
import { AlertTriangle, Calendar, PlayCircle, Loader2 } from "lucide-react";


type UserClassType = {
  startTime: Date | string | number; // unix (seconds or ms)
  endTime: Date | string | number;   // unix (seconds or ms)
  class?: {
    liveStreamUrl?: string;
  };
  // add other fields from the API response if needed
};

export default function LiveStreamRedirect(): JSX.Element {
  const { hash } = useParams<{ hash: string }>();
  const [userClass, setUserClass] = useState<UserClassType | null>(null);
  const [canJoinLiveStream, setCanJoinLiveStream] = useState(false);
  const [endTimePast, setEndTimePast] = useState(true);
  const [timerStarted, setTimerStarted] = useState(false);
  const [timeToClass, setTimeToClass] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!hash) {
      setFetchError("Falta el parámetro hash.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadDetails = async () => {
      setLoading(true);
      setFetchError(null);

      try {
        const response = await getUserClassByHash(hash);

        if (cancelled) return;

        setUserClass(response);

      } catch (err: any) {
        if (!cancelled) {
          setFetchError(err?.message ?? "Error desconocido");
          setLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // only run on mount / hash change
  }, [hash]);

  useEffect(() => {
    if (userClass) {
      setInitialState();
    }
  }, [userClass]);

  const setInitialState = () => {
    const endDate = new Date(userClass?.endTime ?? 0);
    const nowMs = Date.now();
    const endMs = endDate.getTime();
    const isEndPast = endMs < nowMs;

    if (!isEndPast) {
      startTimer();
    } else {
      setEndTimePast(true);
      setLoading(false);
    }
  };

  const startTimer = () => {
    countDown();
    if (!timerRef.current) {
      timerRef.current = window.setInterval(countDown, 1000);
    }
  };

  const countDown = async () => {
    if (!userClass) {
      setLoading(false);
      return;
    }

    const now = new Date();
    const startDate = new Date(userClass.startTime);
    const endDate = new Date(userClass.endTime);

    const joinAvailableStart = subMinutes(startDate, 15);
    const secondsTo = differenceInSeconds(joinAvailableStart, now);
    const canJoin = secondsTo <= 0;
    const endPast = endDate.getTime() < now.getTime();

    if (secondsTo <= 0 && timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!timerStarted && canJoin && !endPast) {
      setLoading(true); // show loader while verifying + redirecting
      await verifyClass(); // attempt verify
      // redirect after small delay so user can see loader
      const liveStreamUrl = userClass?.class?.liveStreamUrl;
      setTimeout(() => {
        if (liveStreamUrl) window.location.href = liveStreamUrl;
      }, 4000);

      setCanJoinLiveStream(true);
      setEndTimePast(false);
      setTimerStarted(true);
      setLoading(false);
      return;
    }

    setCanJoinLiveStream(canJoin);
    setTimeToClass(secondsTo);
    setEndTimePast(endPast);
    setTimerStarted(true);
    setLoading(false);
  };

  const joinLiveStream = async (event?: React.MouseEvent, noDelay?: boolean) => {
    event?.preventDefault();
    setLoading(true);
    await verifyClass();
    const liveStreamUrl = userClass?.class?.liveStreamUrl;
    setTimeout(() => {
      if (liveStreamUrl) window.location.href = liveStreamUrl;
    }, noDelay ? 0 : 4000);
  };

  const verifyClass = async () => {
    if (!hash) return;
    try {
      await verifyUserClassByHash(hash);
    } catch (err) {
      // swallow to match original behaviour; you may want to surface errors here
    }
  };

  const secondsToDhms = (secondsInput: number) => {
    let seconds = Number(secondsInput);
    if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    const dDisplay = d > 0 ? `${d}${d === 1 ? " día, " : " días, "}` : "";
    const hDisplay = h > 0 ? `${h}${h === 1 ? " hora, " : " horas, "}` : "";
    const mDisplay = m > 0 ? `${m}${m === 1 ? " minuto, " : " minutos, "}` : "0 minutos, ";
    const sDisplay = s > 0 ? `${s}${s === 1 ? " segundo" : " segundos"}` : "0 segundos";
    return `${dDisplay}${hDisplay}${mDisplay}${sDisplay}`;
  };

  // Render
  if (!hash) {
    return (
      <div className="px-4 h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-sm font-semibold">No se encontró la clase.</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="px-4 h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="px-4 py-20 h-full flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="text-red-500" size={48} />
          </div>
          <div className="text-lg font-semibold mb-2">
            No se pudieron cargar los detalles de la clase.
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Hubo un problema al obtener la información. Inténtalo de nuevo más
            tarde o regresa al listado de clases.
          </p>
          <a
            href="/user/clases"
            className="inline-block bg-holos-dark text-white py-2 px-4 rounded uppercase"
          >
            Volver a clases
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-20 h-full flex flex-row items-center justify-center">
      <div className="w-full max-w-md">
        {/* Before or during class */}
        {!endTimePast && (
          <div className="text-gray-700 text-center my-6">
            {canJoinLiveStream && timerStarted && (
              <>
                <Loader2 className="animate-spin mx-auto text-holos-dark mb-4" size={40} />
                <div className="text-md font-light text-gray-600">
                  Tu clase online se abrirá momentáneamente.
                  Si no se abre automáticamente, puedes intentar unirte desde el botón.
                </div>
              </>
            )}
            <div className="text-lg font-medium my-4">
              {!canJoinLiveStream && "Tu clase aún no ha comenzado"}
              {canJoinLiveStream && timerStarted && "Tu clase ha comenzado, haz click abajo para unirte."}
            </div>

            <div className="flex justify-center">
              <button
                className={`flex items-center gap-2 py-2 px-4 rounded text-white focus:outline-none ${canJoinLiveStream
                  ? "bg-holos-dark hover:bg-holos-dark/80 cursor-pointer"
                  : "bg-holos-dark/40 cursor-not-allowed"
                  }`}
                onClick={(e) => joinLiveStream(e, true)}
                disabled={!canJoinLiveStream}
              >
                {canJoinLiveStream ? (
                  <>
                    <PlayCircle size={20} />
                    Unirme ahora
                  </>
                ) : (
                  `Unirme en ${secondsToDhms(timeToClass)}`
                )}
              </button>
            </div>
          </div>
        )}

        {/* Class finished */}
        {endTimePast && (
          <div className="text-center">
            <div className="text-4xl mb-4 text-black flex justify-center">
              <Calendar size={50} />
            </div>
            <div className="text-lg font-semibold mb-2">
              Tu clase ya ha terminado.
            </div>
            <p className="text-gray-500 mb-6">
              No puedes unirte a esta sesión, pero siempre puedes reservar otra
              clase en nuestro catálogo.
            </p>
            <a
              href="/user/clases"
              className="inline-block bg-holos-dark text-white py-2 px-4 rounded uppercase"
            >
              Reserva otra clase
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
